'use client';

import React from 'react';
import { OpenViolationCell } from '@/components/ui/OpenViolationCell';
import type { OpenViolationCellSeverity } from '@/components/ui/OpenViolationCell';
import { Divider } from '@/components/ui/Divider';

interface BostonViolation {
  // Common fields that might be available in Boston violation data
  description?: string;
  status?: string;
  date?: string;
  dateCreated?: string;
  dateUpdated?: string;
  violationType?: string;
  address?: string;
  [key: string]: any; // Allow for additional fields
}

interface BostonViolationsModalProps {
  violations: BostonViolation[];
  violationType:
    | 'Housing violations'
    | 'Housing complaints'
    | 'Building and enforcement violations'
    | 'Civic maintenance and sanitation requests';
}

// Map violation type to severity for the OpenViolationCell
const getViolationSeverity = (violationType: string): OpenViolationCellSeverity => {
  switch (violationType) {
    case 'Housing violations':
      return 'highest'; // Most critical
    case 'Housing complaints':
      return 'high'; // High priority
    case 'Building and enforcement violations':
      return 'medium'; // Medium priority
    case 'Civic maintenance and sanitation requests':
      return 'low'; // Lower priority
    default:
      return 'medium';
  }
};

// Format the date from various possible date fields
const formatDate = (violation: BostonViolation) => {
  const dateString = violation.date || violation.dateCreated || violation.dateUpdated;
  if (!dateString) return 'Date not available';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Extract title from violation data
const getViolationTitle = (violation: BostonViolation) => {
  return violation.description || violation.violationType || violation.status || 'Violation';
};

interface MonthGroupProps {
  month: string;
  violations: BostonViolation[];
  violationType: string;
}

function MonthGroup({ month, violations, violationType }: MonthGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      {/* Month header */}
      <div className="px-2 py-3 rounded-lg bg-primary">
        <p className="text-body-2-high text-primary">{month}</p>
      </div>

      {/* Violations for this month */}
      {violations.map((violation, index) => (
        <React.Fragment key={index}>
          <OpenViolationCell
            title={getViolationTitle(violation)}
            date={formatDate(violation)}
            description={violation.description || 'No description available'}
            severity={getViolationSeverity(violationType)}
            showDescription={true}
            isOpen={false}
          />
          {index < violations.length - 1 && (
            <div className="px-2">
              <Divider variant="tertiary" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export function BostonViolationsModal({ violations, violationType }: BostonViolationsModalProps) {
  // Group violations by month/year
  const groupedViolations = React.useMemo(() => {
    const groups: Record<string, BostonViolation[]> = {};

    violations.forEach(violation => {
      try {
        const dateString = violation.date || violation.dateCreated || violation.dateUpdated;
        if (!dateString) {
          // Fallback for violations without dates
          const fallbackKey = 'Unknown Date';
          if (!groups[fallbackKey]) {
            groups[fallbackKey] = [];
          }
          groups[fallbackKey].push(violation);
          return;
        }

        const date = new Date(dateString);
        const monthYear = date.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        });

        if (!groups[monthYear]) {
          groups[monthYear] = [];
        }
        groups[monthYear].push(violation);
      } catch {
        // Fallback for invalid dates
        const fallbackKey = 'Unknown Date';
        if (!groups[fallbackKey]) {
          groups[fallbackKey] = [];
        }
        groups[fallbackKey].push(violation);
      }
    });

    // Sort groups by date (most recent first)
    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Unknown Date') return 1;
      if (b === 'Unknown Date') return -1;

      try {
        const dateA = new Date(a + ' 1'); // Add day for parsing
        const dateB = new Date(b + ' 1');
        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });

    return sortedEntries;
  }, [violations]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        {groupedViolations.map(([month, monthViolations], groupIndex) => (
          <React.Fragment key={month}>
            <MonthGroup month={month} violations={monthViolations} violationType={violationType} />
            {groupIndex < groupedViolations.length - 1 && (
              <div className="px-2 py-1">
                <Divider variant="tertiary" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
